/**
 * Sync - Sync Monitor. Live sync activity and manual runs.
 *
 * Answers, in layout priority order:
 *   1. Current chain status - is a sync running? what failed / completed?
 *   2. Entity status table - dense per-entity snapshot in chain order
 *   3. Active / running operations - which entities are in flight right now
 *   4. Next scheduled run - from admin sync settings (real nextFire* values)
 *
 * Data sources (real APIs only - never fabricate progress / charts / %):
 *   - `GET /api/v1/sync/status`
 *   - `POST /api/v1/sync/trigger?entity=` (manual per-entity Run)
 *   - `GET /api/v1/admin/settings/sync` (next fire + last chain readout)
 *
 * Failed and partial entity rows use left-border flags. Status is colored
 * UI-font text (no badge pills). Timestamps are operator-friendly absolute
 * values (IST) with exact ISO on hover. Polls every ~30s, and ~3s while
 * any entity is running.
 */
import { el, mount } from "../utils/dom";
import { renderPageHeader } from "../components/pageHeader";
import {
  renderLoadingState,
  renderErrorState,
  renderEmptyState,
  renderUnavailableState,
} from "../components/states";
import { renderStatusText, runStatusTone } from "../components/statusText";
import { renderAbsoluteTime, renderDuration } from "../components/dateTime";
import { absoluteTime, entityLabel, ENTITY_ORDER, formatNumber, EMPTY } from "../utils/format";
import { getSyncStatus, triggerSync } from "../services/sync";
import { getSyncSettings } from "../services/admin";
import { ApiError } from "../services/apiClient";
import { confirmAction } from "../components/confirmDialog";
import { showToast } from "../components/toast";
import { HELP, labelWithHelp } from "../components/tooltip";
import type { SyncSettings, SyncStatusRow } from "../types/api";

const REFRESH_INTERVAL_MS = 30_000;
const FAST_REFRESH_INTERVAL_MS = 3_000;

function isRunning(row: SyncStatusRow): boolean {
  if (row.lastRunStatus === "running") return true;
  return Boolean(row.lastRunStartedAt) && !row.lastRunCompletedAt;
}

/** Stable chain order: known entities first (client → … → timesheets), unknowns last. */
function orderedRows(rows: SyncStatusRow[]): SyncStatusRow[] {
  const byEntity = new Map(rows.map((row) => [row.entity, row]));
  const ordered: SyncStatusRow[] = [];
  for (const entity of ENTITY_ORDER) {
    const row = byEntity.get(entity);
    if (row) {
      ordered.push(row);
      byEntity.delete(entity);
    }
  }
  for (const row of byEntity.values()) ordered.push(row);
  return ordered;
}

/** Direct upstream entities required before a standalone sync is fully resolved. */
const ENTITY_DEPENDENCIES: Record<string, readonly string[]> = {
  project: ["client"],
  task: ["project"],
  allocation: ["project", "resource"],
  timesheet_entry: ["project", "resource", "task"],
};

/** Upstream entities that normally sync before this one in the full chain. */
function upstreamEntities(entity: string): string[] {
  return [...(ENTITY_DEPENDENCIES[entity] ?? [])];
}

function dependencyWarning(entity: string): string | null {
  const upstream = upstreamEntities(entity);
  if (!upstream.length) return null;
  const names = upstream.map((e) => entityLabel(e)).join(", ");
  return (
    `${entityLabel(entity)} depends on earlier data in the chain (${names}). ` +
    `Running this alone may leave unresolved links until the next scheduled full sync.`
  );
}

export class SyncStatusPage {
  private intervalId: number | null = null;
  private intervalMs = REFRESH_INTERVAL_MS;
  private busyEntities = new Set<string>();
  /**
   * Entities where a trigger POST returned 202 but the next status refresh
   * has not yet confirmed the transition to "running". Keyed by entity name;
   * value is the timestamp of the trigger so we can expire after 30 s if the
   * backend never transitions (avoids an entity being stuck as busy forever).
   */
  private postTriggerEntities = new Map<string, number>();

  private chainBody: HTMLElement = el("div", {}, [renderLoadingState("Loading chain status…")]);
  private tableBody: HTMLElement = el("div", {}, [renderLoadingState("Loading entity status…")]);
  private activeBody: HTMLElement = el("div", {}, [renderLoadingState("Loading active operations…")]);
  private nextBody: HTMLElement = el("div", {}, [renderLoadingState("Loading schedule…")]);

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    const refreshButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        "aria-label": "Refresh sync status",
        onclick: () => void this.refreshAll(),
      },
      ["Refresh"]
    );

    const warning = el("div", { className: "callout callout--page" }, [
      el("span", { className: "callout__label" }, [
        "Manual runs hit Keka directly. They can take several minutes and may slow down if Keka is rate limiting.",
      ]),
    ]);

    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: "Sync Monitor",
          subtitle: "Live sync activity and manual runs.",
          back: { fallbackPath: "/dashboard", navigate, preferHistory: false, label: "Back to Dashboard" },
          actions: [refreshButton],
        }),
        warning,
        el("div", { className: "panel panel--dominant" }, [
          el("p", { className: "panel__title" }, ["Current chain status"]),
          el("p", { className: "panel__description" }, [
            "Whether a sync is in flight, and how the latest runs came out.",
          ]),
          this.chainBody,
        ]),
        el("div", { className: "panel panel--standard" }, [
          el("p", { className: "panel__title" }, ["Sync Activity & Control"]),
          el("p", { className: "panel__description" }, [
            "Monitor sync performance and run individual syncs across the data pipeline.",
          ]),
          this.tableBody,
        ]),
        el("div", { className: "panel panel--standard" }, [
          el("p", { className: "panel__title" }, ["Active operations"]),
          el("p", { className: "panel__description" }, ["What is syncing right now."]),
          this.activeBody,
        ]),
        el("div", { className: "panel panel--utility" }, [
          el("p", { className: "panel__title" }, ["Next scheduled run"]),
          this.nextBody,
        ]),
      ])
    );

    void this.refreshAll();
    this.scheduleNext();
  }

  destroy(): void {
    if (this.intervalId !== null) {
      window.clearTimeout(this.intervalId);
      this.intervalId = null;
    }
  }

  private scheduleNext(): void {
    if (this.intervalId !== null) window.clearTimeout(this.intervalId);
    this.intervalId = window.setTimeout(() => {
      void this.refreshAll().then(() => this.scheduleNext());
    }, this.intervalMs);
  }

  private async refreshAll(): Promise<void> {
    await Promise.all([this.refreshStatus(), this.refreshSchedule()]);
  }

  private async refreshStatus(): Promise<void> {
    try {
      const rows = await getSyncStatus();
      const ordered = orderedRows(rows);

      // Clear post-trigger guards whose entity is now confirmed running, completed,
      // or that have exceeded the 30-second propagation window.
      for (const [entity, triggerTs] of this.postTriggerEntities) {
        const row = ordered.find((r) => r.entity === entity);
        if (!row || isRunning(row) || Date.now() - triggerTs > 30_000) {
          this.postTriggerEntities.delete(entity);
        }
      }

      // Effective busy = in-flight trigger requests + post-202 propagation guards.
      const effectiveBusy = new Set([...this.busyEntities, ...this.postTriggerEntities.keys()]);
      const running = ordered.filter((r) => isRunning(r) || effectiveBusy.has(r.entity));

      this.intervalMs = running.length > 0 ? FAST_REFRESH_INTERVAL_MS : REFRESH_INTERVAL_MS;

      if (!ordered.length) {
        mount(this.chainBody, renderEmptyState("No sync data yet."));
        mount(this.tableBody, renderEmptyState("No sync data yet."));
        mount(this.activeBody, renderEmptyState("No sync operations are running."));
        return;
      }

      mount(this.chainBody, renderChainStatus(ordered, running));
      mount(
        this.tableBody,
        renderStatusTable(ordered, effectiveBusy, (entity) => void this.runOne(entity))
      );
      mount(this.activeBody, renderActiveOperations(running));
    } catch (err) {
      const retry = { onRetry: () => void this.refreshStatus() };
      mount(this.chainBody, renderErrorState(errorMessage(err), retry));
      mount(this.tableBody, renderErrorState(errorMessage(err), retry));
      mount(this.activeBody, renderErrorState(errorMessage(err), retry));
    }
  }

  private async refreshSchedule(): Promise<void> {
    try {
      const settings = await getSyncSettings();
      mount(this.nextBody, renderNextScheduledRun(settings));
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 503)) {
        mount(this.nextBody, renderUnavailableState(err.message));
      } else {
        mount(
          this.nextBody,
          renderUnavailableState("The next scheduled run could not be loaded.", errorMessage(err))
        );
      }
    }
  }

  private async runOne(entity: string): Promise<void> {
    // Prevent double-trigger: entity is already mid-request or in post-202 propagation window.
    if (this.busyEntities.has(entity) || this.postTriggerEntities.has(entity)) {
      return;
    }

    const warning = dependencyWarning(entity);
    const message = el("div", {}, [
      el("p", {}, [
        `This syncs ${entityLabel(entity)} from Keka now and may take a few minutes.`,
      ]),
      warning ? el("p", { className: "dialog__warning" }, [warning]) : null,
    ]);

    const confirmed = await confirmAction({
      title: `Run ${entityLabel(entity)} sync now?`,
      message,
      confirmLabel: "Run",
    });
    if (!confirmed) return;

    // Re-check after the async confirm dialog - entity may have become busy
    // while the operator was reading the confirmation prompt.
    if (this.busyEntities.has(entity) || this.postTriggerEntities.has(entity)) {
      return;
    }

    this.busyEntities.add(entity);
    void this.refreshStatus();

    try {
      await triggerSync(entity);
      showToast(`Sync started for ${entityLabel(entity)}.`, "success");
      // The 202 only means the trigger was accepted. Guard the entity as busy
      // until the next status refresh confirms the backend has transitioned to
      // "running" (or 30 s elapse, whichever comes first).
      this.postTriggerEntities.set(entity, Date.now());
    } catch (err) {
      showToast(triggerErrorMessage(err), "error");
    } finally {
      this.busyEntities.delete(entity);
      void this.refreshStatus();
    }
  }
}

/* ---------------- Current chain status ---------------- */

function renderChainStatus(rows: SyncStatusRow[], running: SyncStatusRow[]): HTMLElement {
  const failed = rows.filter((r) => r.lastRunStatus === "failed");
  const partial = rows.filter((r) => r.lastRunStatus === "partial");
  const success = rows.filter((r) => r.lastRunStatus === "success");
  const never = rows.filter((r) => !r.lastRunStatus);

  let value = "IDLE";
  let valueClass = "health-panel__primary-value--ok";
  let hint = "No sync is running right now.";

  if (running.length > 0) {
    value = "RUNNING";
    valueClass = "health-panel__primary-value--degraded";
    hint =
      running.length === 1
        ? `${entityLabel(running[0].entity)} is syncing now.`
        : `${running.length} entities are syncing now.`;
  } else if (failed.length > 0) {
    value = "ATTENTION";
    valueClass = "health-panel__primary-value--degraded";
    hint =
      failed.length === 1
        ? `${entityLabel(failed[0].entity)} failed on its last run.`
        : `${failed.length} entities failed on their last run.`;
  } else if (partial.length > 0) {
    value = "PARTIAL";
    valueClass = "health-panel__primary-value--degraded";
    hint =
      partial.length === 1
        ? `${entityLabel(partial[0].entity)} completed partially.`
        : `${partial.length} entities completed partially.`;
  } else if (success.length === rows.length) {
    value = "IDLE";
    valueClass = "health-panel__primary-value--ok";
    hint = "All entities completed successfully on their last run.";
  } else if (never.length === rows.length) {
    value = "IDLE";
    valueClass = "health-panel__primary-value--unknown";
    hint = "No sync runs have been recorded yet.";
  }

  return el("div", { className: "health-panel__body" }, [
    el("div", { className: "health-panel__primary" }, [
      el("span", { className: "health-panel__primary-label" }, ["Chain"]),
      el("span", { className: `health-panel__primary-value ${valueClass}` }, [value]),
      el("span", { className: "health-panel__primary-hint" }, [hint]),
    ]),
    el("div", { className: "health-panel__checks" }, [
      summaryCheck("Running", String(running.length), running.length > 0 ? "accent" : "muted"),
      summaryCheck("Failed", String(failed.length), failed.length > 0 ? "danger" : "muted"),
      summaryCheck(
        labelWithHelp("Partial", HELP.partial),
        String(partial.length),
        partial.length > 0 ? "accent" : "muted"
      ),
      summaryCheck("Completed", String(success.length), success.length > 0 ? "success" : "muted"),
    ]),
  ]);
}

function summaryCheck(
  label: string | HTMLElement,
  value: string,
  tone: "success" | "danger" | "accent" | "muted"
): HTMLElement {
  return el("div", { className: "health-check" }, [
    el("div", { className: "health-check__label" }, [label]),
    renderStatusText(value, tone, { dot: false }),
  ]);
}

/* ---------------- Entity status table ---------------- */

function renderStatusTable(
  rows: SyncStatusRow[],
  busyEntities: Set<string>,
  onRun: (entity: string) => void
): HTMLElement {
  const table = el("table", { className: "data-table" }, [
    el("thead", {}, [
      el("tr", {}, [
        el("th", { scope: "col", className: "data-table__col--entity" }, ["Data type"]),
        el("th", { scope: "col", className: "data-table__col--status" }, ["Status"]),
        el("th", { scope: "col", className: "data-table__col--time" }, ["Started"]),
        el("th", { scope: "col", className: "data-table__col--time" }, ["Finished"]),
        el("th", { scope: "col", className: "data-table__col--duration" }, ["Duration"]),
        el("th", { className: "numeric data-table__col--numeric", scope: "col" }, ["Processed"]),
        el("th", { className: "numeric data-table__col--numeric", scope: "col" }, [
          labelWithHelp("Skipped", HELP.skipped),
        ]),
        el("th", { scope: "col", className: "data-table__col--time" }, [
          labelWithHelp("Last Synced", HELP.watermark),
        ]),
        el("th", { scope: "col", className: "data-table__actions-col" }, [""]),
      ]),
    ]),
    el(
      "tbody",
      {},
      rows.map((row) => {
        const tone = runStatusTone(row.lastRunStatus);
        const flagClass =
          row.lastRunStatus === "failed"
            ? "flag-danger"
            : row.lastRunStatus === "partial"
              ? "flag-accent"
              : "";
        const watermark =
          row.lastSuccessfulTimesheetWindowEnd ?? row.lastSuccessfulKekaLastModifiedWatermark;
        const running = isRunning(row) || busyEntities.has(row.entity);
        return el("tr", { className: flagClass || undefined }, [
          el("td", {}, [entityLabel(row.entity)]),
          el("td", {}, [renderStatusText(tone.label, tone.tone, { pulse: tone.pulse })]),
          el("td", {}, [renderAbsoluteTime(row.lastRunStartedAt)]),
          el("td", {}, [renderAbsoluteTime(row.lastRunCompletedAt)]),
          el("td", {}, [renderDuration(row.lastRunStartedAt, row.lastRunCompletedAt)]),
          el("td", { className: "numeric tnum" }, [formatNumber(row.recordsProcessed)]),
          el("td", { className: "numeric tnum" }, [formatNumber(row.recordsSkipped)]),
          el("td", {}, [renderAbsoluteTime(watermark)]),
          el("td", { className: "data-table__actions-cell" }, [
            el(
              "button",
              {
                className: "btn btn-ghost btn-sm",
                type: "button",
                disabled: running,
                "aria-label": running
                  ? `${entityLabel(row.entity)} sync is running`
                  : `Run ${entityLabel(row.entity)} sync`,
                onclick: () => onRun(row.entity),
              },
              [running ? "Running…" : "Run"]
            ),
          ]),
        ]);
      })
    ),
  ]);

  return el("div", { className: "data-table-wrap data-table-wrap--fit" }, [table]);
}

/* ---------------- Active / running operations ---------------- */

function renderActiveOperations(running: SyncStatusRow[]): HTMLElement {
  if (!running.length) {
    return renderEmptyState("No sync operations are running.", {
      hint: "Active syncs will show up here.",
    });
  }

  const table = el("table", { className: "data-table" }, [
    el("thead", {}, [
      el("tr", {}, [
        el("th", { scope: "col", className: "data-table__col--entity" }, ["Data type"]),
        el("th", { scope: "col", className: "data-table__col--status" }, ["Status"]),
        el("th", { scope: "col", className: "data-table__col--time" }, ["Started"]),
        el("th", { scope: "col", className: "data-table__col--numeric" }, ["Run id"]),
      ]),
    ]),
    el(
      "tbody",
      {},
      running.map((row) =>
        el("tr", { className: "flag-accent" }, [
          el("td", {}, [entityLabel(row.entity)]),
          el("td", {}, [renderStatusText("running", "accent", { pulse: true })]),
          el("td", {}, [renderAbsoluteTime(row.lastRunStartedAt)]),
          el("td", {}, [
            el("span", { className: "mono tnum" }, [
              row.runId !== null && row.runId !== undefined ? `#${row.runId}` : EMPTY,
            ]),
          ]),
        ])
      )
    ),
  ]);

  return el("div", { className: "data-table-wrap data-table-wrap--fit" }, [table]);
}

/* ---------------- Next scheduled run ---------------- */

function renderNextScheduledRun(settings: SyncSettings): HTMLElement {
  const nextNode = settings.nextFireAtIst
    ? el("div", { className: "callout", title: settings.nextFireAtUtc ?? settings.nextFireAtIst }, [
        el("span", { className: "callout__label" }, ["Next run"]),
        el("span", { className: "callout__value" }, [absoluteTime(settings.nextFireAtIst)]),
      ])
    : el("p", { className: "next-cron__empty" }, [
        settings.syncEnabled ? "Not yet computed." : "Sync is turned off, so nothing is scheduled.",
      ]);

  return el("div", {}, [
    nextNode,
    el("div", { className: "next-cron next-cron__follow-up" }, [
      cronRow(
        "Schedule",
        el("span", {}, [
          settings.syncEnabled ? `${settings.scheduleKind} · ${settings.scheduleTime} IST` : "disabled",
        ])
      ),
      cronRow(
        "Last chain",
        settings.lastChainStatus
          ? renderStatusText(...statusToneTuple(settings.lastChainStatus))
          : el("span", {}, [EMPTY])
      ),
      cronRow("Last completed", renderAbsoluteTime(settings.lastChainCompletedAt)),
    ]),
  ]);
}

function cronRow(label: string, value: HTMLElement): HTMLElement {
  return el("div", { className: "next-cron__row" }, [
    el("span", { className: "next-cron__label" }, [label]),
    value,
  ]);
}

function statusToneTuple(status: string): [string, "success" | "danger" | "accent" | "muted"] {
  const tone = runStatusTone(status);
  return [tone.label, tone.tone];
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong loading this data.";
}

function triggerErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 401) {
    return "You are not signed in. Sign in with Microsoft to continue.";
  }
  if (err instanceof ApiError && err.status === 404) {
    return "Manual sync is turned off on this server.";
  }
  if (err instanceof ApiError) return err.message;
  return "Could not start the sync run.";
}
